/**
 * Database manager.
 * Tauri desktop: SQLite via @tauri-apps/plugin-sql.
 * Web/PWA: IndexedDB wrapper implementing the same DatabaseConnection interface.
 */

import { MIGRATION_001_UP } from './migrations/001_initial';
import { MIGRATION_002_UP } from './migrations/002_investigations';
import { MIGRATION_003_UP } from './migrations/003_hardening';
import { isTauriRuntime } from '@/utils/tauri';

export interface DatabaseConnection {
  execute(sql: string, params?: unknown[]): Promise<void>;
  query<T = unknown>(sql: string, params?: unknown[]): Promise<T[]>;
  close(): Promise<void>;
}

let db: DatabaseConnection | null = null;

// ---------------------------------------------------------------------------
// SQLite adapter (Tauri desktop)
// ---------------------------------------------------------------------------

async function initSQLite(): Promise<DatabaseConnection> {
  // Dynamic path variable prevents Vite from statically analyzing this Tauri-only import
  const tauriSqlPlugin = '@tauri-apps/plugin-sql';
  const Database = (await import(/* @vite-ignore */ tauriSqlPlugin)).default;
  const sqlite = await Database.load('sqlite:sentinel.db');

  const connection: DatabaseConnection = {
    async execute(sql: string, params?: unknown[]): Promise<void> {
      await sqlite.execute(sql, params ?? []);
    },
    async query<T = unknown>(sql: string, params?: unknown[]): Promise<T[]> {
      return sqlite.select(sql, params ?? []) as Promise<T[]>;
    },
    async close(): Promise<void> {
      await sqlite.close();
    },
  };

  return connection;
}

// ---------------------------------------------------------------------------
// IndexedDB adapter (web/PWA fallback)
// ---------------------------------------------------------------------------

const IDB_NAME = 'sentinel';
const IDB_VERSION = 3;
const IDB_STORE = 'entities';

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const idb = req.result;
      if (!idb.objectStoreNames.contains(IDB_STORE)) {
        idb.createObjectStore(IDB_STORE, { keyPath: 'id' });
      }
      // Phase 1 stores
      for (const store of ['relationships', 'alert_rules', 'alert_history', 'collection_log', 'workspaces', 'geofences', 'source_events']) {
        if (!idb.objectStoreNames.contains(store)) {
          idb.createObjectStore(store, { keyPath: 'id' });
        }
      }
      // Phase 2 investigation stores
      for (const store of ['investigations', 'investigation_entities', 'collection_tasks', 'investigation_log', 'entity_notes']) {
        if (!idb.objectStoreNames.contains(store)) {
          idb.createObjectStore(store, { keyPath: 'id' });
        }
      }
      // Phase 2 hardening stores (migration 003)
      for (const store of ['canonical_entities', 'canonical_identifiers', 'entity_canonical_map', 'staging_proposals', 'assessment_versions']) {
        if (!idb.objectStoreNames.contains(store)) {
          idb.createObjectStore(store, { keyPath: 'id' });
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Minimal IndexedDB wrapper that implements DatabaseConnection.
 * Supports basic CRUD via a simplified SQL-like interface.
 * This is NOT a full SQL engine — it handles the specific queries
 * used by EntityStore (INSERT, SELECT, UPDATE, DELETE on single tables).
 */
function createIndexedDBConnection(idb: IDBDatabase): DatabaseConnection {
  // In-memory stores keyed by table name for simpler query support
  const memTables = new Map<string, Map<string, Record<string, unknown>>>();

  function getTable(name: string): Map<string, Record<string, unknown>> {
    if (!memTables.has(name)) {
      memTables.set(name, new Map());
    }
    return memTables.get(name)!;
  }

  // Parse table name from simple SQL statements
  function parseTable(sql: string): string | null {
    const insertMatch = sql.match(/INSERT\s+INTO\s+(\w+)/i);
    if (insertMatch) return insertMatch[1];
    const selectMatch = sql.match(/FROM\s+(\w+)/i);
    if (selectMatch) return selectMatch[1];
    const updateMatch = sql.match(/UPDATE\s+(\w+)/i);
    if (updateMatch) return updateMatch[1];
    const deleteMatch = sql.match(/DELETE\s+FROM\s+(\w+)/i);
    if (deleteMatch) return deleteMatch[1];
    return null;
  }

  return {
    async execute(sql: string, params?: unknown[]): Promise<void> {
      // Handle CREATE TABLE/INDEX silently (schema is managed by IDB upgrades)
      if (/^\s*(CREATE|DROP)\s/i.test(sql)) return;

      const tableName = parseTable(sql);
      if (!tableName) return;

      const table = getTable(tableName);

      // INSERT
      if (/^\s*INSERT/i.test(sql)) {
        const colMatch = sql.match(/\(([^)]+)\)\s*VALUES\s*\(([^)]+)\)/i);
        if (colMatch && params) {
          const cols = colMatch[1].split(',').map(c => c.trim());
          const row: Record<string, unknown> = {};
          cols.forEach((col, i) => { row[col] = params[i]; });
          const id = (row.id as string) ?? '';
          table.set(id, row);

          // Persist to IDB if store exists
          if (idb.objectStoreNames.contains(tableName)) {
            const tx = idb.transaction(tableName, 'readwrite');
            tx.objectStore(tableName).put(row);
          }
        }
        return;
      }

      // UPDATE
      if (/^\s*UPDATE/i.test(sql)) {
        const setMatch = sql.match(/SET\s+(.+?)\s+WHERE/i);
        const whereMatch = sql.match(/WHERE\s+id\s*=\s*\$(\d+)/i);
        if (setMatch && whereMatch && params) {
          const idIdx = parseInt(whereMatch[1]) - 1;
          const id = params[idIdx] as string;
          const existing = table.get(id);
          if (existing) {
            const setParts = setMatch[1].split(',').map(s => s.trim());
            for (const part of setParts) {
              const [col, placeholder] = part.split('=').map(s => s.trim());
              const pIdx = parseInt(placeholder.replace('$', '')) - 1;
              existing[col] = params[pIdx];
            }
            table.set(id, existing);
            if (idb.objectStoreNames.contains(tableName)) {
              const tx = idb.transaction(tableName, 'readwrite');
              tx.objectStore(tableName).put(existing);
            }
          }
        }
        return;
      }

      // DELETE
      if (/^\s*DELETE/i.test(sql)) {
        const whereMatch = sql.match(/WHERE\s+(?:id|source_entity_id|target_entity_id)\s*=\s*\$(\d+)/i);
        if (whereMatch && params) {
          const idIdx = parseInt(whereMatch[1]) - 1;
          const id = params[idIdx] as string;

          // For entity deletes, remove by id
          if (/WHERE\s+id\s*=/i.test(sql)) {
            table.delete(id);
            if (idb.objectStoreNames.contains(tableName)) {
              const tx = idb.transaction(tableName, 'readwrite');
              tx.objectStore(tableName).delete(id);
            }
          } else {
            // For relationship cascade deletes (source_entity_id or target_entity_id)
            for (const [key, row] of table) {
              if (row.source_entity_id === id || row.target_entity_id === id) {
                table.delete(key);
                if (idb.objectStoreNames.contains(tableName)) {
                  const tx = idb.transaction(tableName, 'readwrite');
                  tx.objectStore(tableName).delete(key);
                }
              }
            }
          }
        }
        return;
      }
    },

    async query<T = unknown>(sql: string, params?: unknown[]): Promise<T[]> {
      // Handle CREATE/DROP
      if (/^\s*(CREATE|DROP)\s/i.test(sql)) return [] as T[];

      const tableName = parseTable(sql);
      if (!tableName) return [] as T[];

      const table = getTable(tableName);
      let rows = Array.from(table.values());

      // WHERE clause filtering
      if (params && params.length > 0) {
        const whereMatch = sql.match(/WHERE\s+(.+?)(?:\s+ORDER|\s+LIMIT|\s*$)/i);
        if (whereMatch) {
          const conditions = whereMatch[1];

          // id = $N
          const idMatch = conditions.match(/id\s*=\s*\$(\d+)/i);
          if (idMatch) {
            const val = params[parseInt(idMatch[1]) - 1];
            rows = rows.filter(r => r.id === val);
          }

          // type = $N
          const typeMatch = conditions.match(/type\s*=\s*\$(\d+)/i);
          if (typeMatch) {
            const val = params[parseInt(typeMatch[1]) - 1];
            rows = rows.filter(r => r.type === val);
          }

          // watch_status = $N
          const watchMatch = conditions.match(/watch_status\s*=\s*\$(\d+)/i);
          if (watchMatch) {
            const val = params[parseInt(watchMatch[1]) - 1];
            rows = rows.filter(r => r.watch_status === val);
          }

          // name LIKE $N
          const likeMatch = conditions.match(/name\s+LIKE\s+\$(\d+)/i);
          if (likeMatch) {
            const val = (params[parseInt(likeMatch[1]) - 1] as string).replace(/%/g, '');
            rows = rows.filter(r => (r.name as string || '').toLowerCase().includes(val.toLowerCase()));
          }

          // source_entity_id = $N OR target_entity_id = $N
          const srcMatch = conditions.match(/source_entity_id\s*=\s*\$(\d+)/i);
          const tgtMatch = conditions.match(/target_entity_id\s*=\s*\$(\d+)/i);
          if (srcMatch && tgtMatch) {
            const srcVal = params[parseInt(srcMatch[1]) - 1];
            const tgtVal = params[parseInt(tgtMatch[1]) - 1];
            rows = rows.filter(r => r.source_entity_id === srcVal || r.target_entity_id === tgtVal);
          } else if (srcMatch) {
            const val = params[parseInt(srcMatch[1]) - 1];
            rows = rows.filter(r => r.source_entity_id === val);
          }
        }
      }

      // ORDER BY
      if (/ORDER\s+BY\s+timestamp/i.test(sql)) {
        rows.sort((a, b) => String(a.timestamp ?? '').localeCompare(String(b.timestamp ?? '')));
      }

      // LIMIT
      const limitMatch = sql.match(/LIMIT\s+\$(\d+)/i);
      if (limitMatch && params) {
        const limit = params[parseInt(limitMatch[1]) - 1] as number;
        rows = rows.slice(0, limit);
      }

      return rows as T[];
    },

    async close(): Promise<void> {
      idb.close();
    },
  };
}

async function initIndexedDB(): Promise<DatabaseConnection> {
  const idb = await openIDB();
  return createIndexedDBConnection(idb);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Initialize the database — SQLite on Tauri, IndexedDB on web. */
export async function initDatabase(): Promise<void> {
  if (db) return; // Already initialized

  try {
    if (isTauriRuntime()) {
      db = await initSQLite();
      console.log('[Sentinel] SQLite database initialized');
    } else {
      db = await initIndexedDB();
      console.log('[Sentinel] IndexedDB database initialized');
    }

    // Run migrations (SQLite executes DDL, IndexedDB adapter ignores CREATE TABLE)
    const migrations = [
      { name: '001_initial', sql: MIGRATION_001_UP },
      { name: '002_investigations', sql: MIGRATION_002_UP },
      { name: '003_hardening', sql: MIGRATION_003_UP },
    ];

    for (const migration of migrations) {
      const statements = migration.sql
        .split(';')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      for (const stmt of statements) {
        await db.execute(stmt + ';');
      }
      console.log(`[Sentinel] Database migration ${migration.name} applied`);
    }
  } catch (err) {
    console.error('[Sentinel] Database initialization failed:', err);
    throw err;
  }
}

/** Get the current database connection, or null if not initialized. */
export function getDatabase(): DatabaseConnection | null {
  return db;
}

/**
 * Set the database connection (called by Tauri bootstrap in desktop mode).
 * @internal
 */
export function setDatabase(connection: DatabaseConnection): void {
  db = connection;
}
